import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { Button, Heading, Image, Paragraph } from '@app/shared/ui/atoms/Common';
import { SettlementInfoCard } from '@app/shared/ui/molecules/Home';
import { cn, telegramVibrate } from '@app/shared/utils/common';
import { Settlement } from '@app/store/slices/settlements/settlementsTypes';
import { giftImage, homeCalculatorIcon } from '@app/assets';
import { Modal, Tutorial } from '@app/shared/ui/organisms/Common';
import { useBalance } from '@app/shared/hooks/useBalance';
import { PriceType } from '@app/shared/types/common';
import notification from '@app/shared/utils/notification';
import { useGetCurrentUserQuery, useSendGiftMutation } from '@app/store/slices/users/usersApi';
import { SendGiftBody } from '@app/store/slices/users/usersTypes';
import { useApiErrorHandler } from '@app/shared/hooks/useApiErrorHandler';
import { CountSelect } from '@app/shared/ui/molecules/ProfitCalculator';
import { useSettlementModal } from '@app/shared/hooks/useSettlementModal';
import { SettlementModal } from '@app/shared/ui/organisms/Home/SettlementModal';
import { TG_BOT_URL } from '@app/shared/constants/api';
import { useCopyToClipboard } from 'usehooks-ts';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { tutorialActions } from '@app/store/slices/tutorial/tutorialSlice';

interface SettlementsSectionProps {
  containerClassName?: string;
  onCalculatorBtnClick: () => void;
  onQuestionBtnClick: () => void;
  settlements: Settlement[];
}

const SETTLEMENT_ID_PREFIX = 'settlement__';

export function SettlementsSection({
  containerClassName,
  onCalculatorBtnClick,
  onQuestionBtnClick,
  settlements,
}: SettlementsSectionProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const tutorial = useAppSelector((state) => state.tutorial);
  const { data: user } = useGetCurrentUserQuery();
  const [giftsCount, setGiftsCount] = useState<string | undefined>('1');
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [giftNodeLevel, setGiftNodeLevel] = useState<number | undefined>(0);
  const [sendGift] = useSendGiftMutation();
  const { isBalanceEnough, fetchBalance } = useBalance();
  const { handleFetchError } = useApiErrorHandler();
  const [, copyToClipboard] = useCopyToClipboard();

  const {
    settlementModalState,
    handleSettlementModalOpen,
    handleSettlementModalClose,
    toggleIsBoosting,
    handlePurchaseSettlement,
    handleSettlementBoost,
    isPurchasingSettlement,
    isBoostingSettlement,
  } = useSettlementModal();

  const handleSendGift = async () => {
    if (!user) {
      throw new Error('No user');
    }
    telegramVibrate();
    if (!giftsCount) {
      return;
    }
    if (giftNodeLevel === undefined) {
      return;
    }

    if (!isBalanceEnough(PriceType.TON, settlements[giftNodeLevel].price * Number(giftsCount))) {
      notification.error(t('errors.balance'));
      return;
    }

    const giftBody: SendGiftBody = {
      to: Number(giftsCount),
      action: 'SETTLEMENT',
      nodeLevel: giftNodeLevel,
      balance: 'ton',
      maxWaiting: 1440,
    };

    try {
      const res = await sendGift(giftBody).unwrap();
      const token = res.data?.giftToken;
      if (!token) {
        throw new Error('No gift token');
      }
      const link = `${TG_BOT_URL}?start=${String(user.id)}-gift-${token}`;
      await copyToClipboard(link);
      notification.success(t('home.gift_link_copied'));
      await fetchBalance();
    } catch (err: unknown) {
      handleFetchError(err);
    }
  };

  return (
    <section className={cn('flex flex-col gap-5', containerClassName)}>
      <div className="flex items-center justify-between">
        <Heading className="text-[22px] font-bold text-foreground">
          {t('home.development_settlements')}
        </Heading>
        <div className="flex items-center gap-2">
          <Tutorial
            text={t('tutorial.calculator.1_step')}
            enabled={tutorial.step === 'HOME_CALC_1'}
          >
            <Button
              variant="custom"
              className="flex size-[34px] items-center justify-center rounded-lg bg-[#131F4F]"
              onClick={() => {
                if (tutorial.step === 'HOME_CALC_1') {
                  dispatch(tutorialActions.onCalculatorClick());
                }
                onCalculatorBtnClick();
              }}
            >
              <img src={homeCalculatorIcon} alt="calculator" className="w-5" />
            </Button>
          </Tutorial>
          <Button
            variant="custom"
            onClick={onQuestionBtnClick}
            className={cn(
              'flex items-center justify-center w-[34px] h-[34px] rounded-lg bg-[#23306669] text-base font-semibold text-home-card-number-text'
            )}
          >
            <div className="bg-[linear-gradient(45.92deg,#187FF5_7.15%,#BBFFEA_93.99%)] bg-clip-text text-transparent">
              AI
            </div>
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {settlements.map((item, idx, arr) => {
          const isLastItem = idx === arr.length - 1;
          const isFirstItem = item === arr[0];
          const isSecondItem = item === arr[1];
          const isThirdItem = item === arr[2];
          const isFourthItem = item === arr[3];
          const isTutorial = tutorial.step === 'HOME_SETTLEMENT_1' && isFirstItem;

          const shouldPulse =
            (isFirstItem && !item.isPurchased) || (!item.isPurchased && arr[idx - 1]?.isPurchased);

          return (
            <SettlementInfoCard
              key={item.id}
              id={`${SETTLEMENT_ID_PREFIX}${String(item.id)}`}
              settlement={item}
              align={idx % 2 === 0 ? 'left' : 'right'}
              withConnector={!isLastItem}
              isNextSettlementPurchased={!isLastItem && arr[idx + 1].isPurchased}
              shouldPulse={shouldPulse && !isTutorial}
              onClick={handleSettlementModalOpen}
              isFirstItem={isFirstItem}
              isSecondItem={isSecondItem}
              isThirdItem={isThirdItem}
              isFourthItem={isFourthItem}
              onGiftClick={(nodeLevel: number) => {
                if (isTutorial) {
                  return;
                }
                setGiftNodeLevel(nodeLevel);
                setIsGiftModalOpen(true);
              }}
            />
          );
        })}
      </div>

      {settlementModalState.settlement && (
        <SettlementModal
          isOpen={settlementModalState.isOpen}
          onOpenChange={handleSettlementModalClose}
          settlement={settlementModalState.settlement}
          actionType={settlementModalState.action}
          onBoostBtnClick={handleSettlementBoost}
          onBuyBtnClick={handlePurchaseSettlement}
          isBoostBtnDisabled={isBoostingSettlement}
          isBuyBtnDisabled={isPurchasingSettlement}
          isBoosting={settlementModalState.isBoosting}
          toggleIsBoosting={toggleIsBoosting}
        />
      )}

      <Modal
        isOpen={isGiftModalOpen}
        onOpenChange={setIsGiftModalOpen}
        featuredImg={<Image src={giftImage} />}
        hiddenTitle="Send gift"
        contentClassName="shadow-blue"
      >
        <Paragraph className="whitespace-pre-line">
          {t('gift_modal.text_settlement', {
            settlement:
              giftNodeLevel === 0
                ? t('settlements.titles.land')
                : giftNodeLevel === 1
                  ? t('settlements.titles.camping')
                  : giftNodeLevel === 2
                    ? t('settlements.titles.hut')
                    : t('settlements.titles.cottage'),
          })}
        </Paragraph>

        <CountSelect
          value={giftsCount}
          onChange={setGiftsCount}
          length={10}
          itemKey="gift_modal.gift"
        />
        <Paragraph size="xs">{t('gift_modal.input_label_settlement')}</Paragraph>
        <Paragraph size="xs">{t('gift_modal.link_expires')}</Paragraph>

        <div className="flex gap-2">
          <Button onClick={handleSendGift}>{t('common.generate_link')}</Button>
        </div>
      </Modal>
    </section>
  );
}
